// src/services/scanPointService.js
// ---------------------------------------------------------------------------
// Resolves who is holding the code the guest just scanned.
//
// This used to read the bartenders collection directly, which the Firestore
// rules have never allowed the customer app to do: team records carry names and
// email addresses and are portal only. The read threw a permission error, the
// surrounding condition never ran, and scans could be recorded with no name
// attached to them.
//
// The scan point document already stores the team member's display name and is
// public by design, so that is what the guest flow reads now. No personal
// contact detail ever reaches the guest's browser.
// ---------------------------------------------------------------------------
import { collection, getDocs, limit, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { holderFromLegacy } from "../config/holder";

/**
 * `role` is the resolved label, already worked out by the portal from the trade
 * channel and the person's role, and stored here. It is deliberately not
 * derived in this app: the taxonomy lives in one file in the portal and a
 * second copy would drift.
 *
 * `person` is false for a placement, a shelf code, a listing or a kiosk. There
 * is nobody holding those, so the guest screen must not name one.
 *
 * @returns {Promise<{name: string, role: string, person: boolean}>}
 */
export const resolveTeamMember = async (eventId, memberId) => {
  const none = { name: "", role: "", person: false, holderKind: "none", holderName: "" };
  if (!eventId || !memberId) return none;
  try {
    const snap = await getDocs(
      query(
        collection(db, "qrCodes"),
        where("eventId", "==", eventId),
        where("bartenderId", "==", memberId),
        limit(1)
      )
    );
    if (snap.empty) return none;
    const d = snap.docs[0].data();
    // holderKind is what the portal chose. Absent means a scan point written
    // before the kind existed, and holderFromLegacy reads it conservatively:
    // a named person stays a person, everything else becomes nobody.
    const legacy = holderFromLegacy(d);
    return {
      name: d.bartenderName || "",
      role: d.roleLabel || "",
      person: d.rolePerson === true && !!d.bartenderName,
      holderKind: d.holderKind || legacy.kind,
      holderName:
        typeof d.holderName === "string" ? d.holderName : legacy.name,
    };
  } catch (error) {
    // Never block the guest on this: an unnamed scan is better than no scan.
    console.error("Could not resolve the team member:", error);
    return none;
  }
};
